import React from "react";
import { Box, Typography, Button, Card, CardContent, Grid, Container, Stack, Chip, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  Timer as TimerIcon,
  RocketLaunch,
  Verified,
  FlashOn,
  Shield,
  Devices,
  DirectionsCar,
  Autorenew,
  PlayArrow,
} from "@mui/icons-material";

const Homepage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      background: isDark 
        ? "radial-gradient(1200px 500px at 70% -10%, rgba(81,243,210,0.14), transparent), radial-gradient(800px 400px at 10% 10%, rgba(67,57,242,0.14), transparent), linear-gradient(180deg, #0a0d1a 0%, #070a16 100%)"
        : "radial-gradient(1200px 500px at 70% -10%, rgba(81,243,210,0.12), transparent), radial-gradient(800px 400px at 10% 10%, rgba(67,57,242,0.12), transparent), linear-gradient(180deg, #f0f4f8 0%, #e1e8ed 100%)",
      color: isDark ? "#fff" : "#0a2540",
    }}>
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: isDark 
          ? `radial-gradient(2px 2px at 20% 30%, rgba(255,255,255,0.25) 50%, transparent 51%),
             radial-gradient(1.5px 1.5px at 70% 40%, rgba(255,255,255,0.18) 50%, transparent 51%),
             radial-gradient(1.5px 1.5px at 40% 70%, rgba(255,255,255,0.18) 50%, transparent 51%)`
          : `radial-gradient(2px 2px at 20% 30%, rgba(0,0,0,0.15) 50%, transparent 51%),
             radial-gradient(1.5px 1.5px at 70% 40%, rgba(0,0,0,0.1) 50%, transparent 51%),
             radial-gradient(1.5px 1.5px at 40% 70%, rgba(0,0,0,0.1) 50%, transparent 51%)`,
        backgroundRepeat: 'no-repeat'
      }} />

  <Container maxWidth={false} sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 6 } }}>
        {/* Hero */}
        <Box sx={{ textAlign: "center", py: { xs: 8, md: 12 } }}>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
            <Chip 
              icon={<RocketLaunch sx={{ color: '#51f3d2' }}/>} 
              label="Intentional device use" 
              sx={{ 
                bgcolor: isDark ? 'rgba(81,243,210,0.08)' : 'rgba(81,243,210,0.15)', 
                color: isDark ? '#bff7ee' : '#0a7d6e', 
                borderColor: 'rgba(81,243,210,0.3)', 
                border: '1px solid' 
              }} 
            />
            <Chip 
              icon={<Verified sx={{ color: '#51f3d2' }}/>} 
              label="Google sign-in" 
              sx={{ 
                bgcolor: isDark ? 'rgba(81,243,210,0.08)' : 'rgba(81,243,210,0.15)', 
                color: isDark ? '#bff7ee' : '#0a7d6e', 
                borderColor: 'rgba(81,243,210,0.3)', 
                border: '1px solid' 
              }} 
            />
          </Stack>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(90deg,#51f3d2 0%, #8bdcf8 45%, #e7f1ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            timer.quest
          </Typography>
          <Typography variant="h6" sx={{ color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(10,37,64,0.8)', maxWidth: 720, mx: 'auto', mb: 4 }}>
            Make every session deliberate. Set an intention, choose a clear time boundary, and let your devices stay annoying until you do what you said you would do.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/login?google=1"
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              sx={{
                background: 'linear-gradient(90deg,#4339F2 0%, #6b63ff 100%)',
                boxShadow: '0 10px 30px rgba(67,57,242,0.4)'
              }}
            >
              Start intentionally
            </Button>
          </Stack>
        </Box>

        {/* Features */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {[
            { icon: <TimerIcon sx={{ fontSize: 36, color: '#51f3d2' }} />, title: 'Bound every session', desc: 'Choose a duration first so your device use has a clear stopping point.' },
            { icon: <Devices sx={{ fontSize: 36, color: '#8bdcf8' }} />, title: 'Native blockers', desc: 'Android, macOS, Linux, and iOS clients are designed to keep asking what you are doing and for how long.' },
            { icon: <Autorenew sx={{ fontSize: 36, color: '#c9b5ff' }} />, title: 'Restart quickly', desc: 'Return to previous intentions after a break without re-entering the same task every time.' },
            { icon: <DirectionsCar sx={{ fontSize: 36, color: '#ffd257' }} />, title: 'Support real life', desc: 'Set intentional sessions for practical tasks like navigation, errands, or admin work without opening the door to everything else.' },
            { icon: <FlashOn sx={{ fontSize: 36, color: '#f598ff' }} />, title: 'Fast by default', desc: 'Common durations are one tap away, and the custom path stays available when you need it.' },
            { icon: <Shield sx={{ fontSize: 36, color: '#9fe2c7' }} />, title: 'Private by default', desc: 'Your intentions, timer history, and settings stay tied to your own account.' },
          ].map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
        <Card sx={{ 
          height: '100%', 
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(10,37,64,0.04)', 
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,37,64,0.12)'}`, 
          backdropFilter: 'blur(6px)' 
        }}>
                <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {f.icon}
            <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#0a2540' }}>{f.title}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(10,37,64,0.85)' }}>{f.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* How it works */}
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: isDark ? '#fff' : '#0a2540' }}>How it works</Typography>
          <Grid container spacing={2}>
            {[{ n: '1', t: 'Name the intention', d: 'State what you are about to do before your device gets out of the way.' },
              { n: '2', t: 'Pick a duration', d: 'Use a preset timebox or custom duration so the session has a boundary.' },
              { n: '3', t: 'Do only that', d: 'Your blocker stays involved until the timer is running and the session stays active.' },
              { n: '4', t: 'Adjust with intention', d: 'Need longer? Add time deliberately instead of drifting into unrelated use.' }].map((s) => (
                <Grid item xs={12} md={3} key={s.n}>
                  <Card sx={{ 
                    height: '100%', 
                    textAlign: 'left', 
                    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(10,37,64,0.04)', 
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,37,64,0.12)'}` 
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#51f3d2', mb: 1 }}>Step {s.n}</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, color: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(10,37,64,0.95)' }}>{s.t}</Typography>
                      <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.92)' : 'rgba(10,37,64,0.85)' }}>{s.d}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', pb: 10 }}>
          <Typography variant="h5" sx={{ mb: 2, color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(10,37,64,0.9)' }}>
            Ready to make your devices a little more boring?
          </Typography>
          <Button component={RouterLink} to="/login?google=1" variant="contained" size="large" startIcon={<PlayArrow />}
            sx={{ background: 'linear-gradient(90deg,#4339F2 0%, #6b63ff 100%)' }}>
            Start intentionally
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 4, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(10,37,64,0.6)' }}>
          <Typography variant="caption">Built on Firebase • Your data is yours • timer.quest</Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Homepage; 